import type { SearchState } from '@reducers/search';
import type { ListingQuery } from '@shared/routes';
import type { SeoRequest } from '@client/components/SEO/SeoClient';

/**
 * Filters which should not be considered for SEO purposes.
 */
const deniedFilters = [
  'c',
  'lim',
  'qso',
  'sort',
  'start',
  't',
  'q',
  'r',
  'ci',
  'to',
  'shp',
  'urg',
];

/**
 * A mapping to special "fake" filter names used on Meti from the filters in
 * the Subito URL.
 */
const importantFilters = new Map<string, string>([
  ['cb', 'carBrand'],
  ['cm', 'carModel'],
  ['ct', 'carType'],
  ['fl', 'carFuel'],
  ['bb', 'motorbikeBrand'],
  ['bm', 'motorbikeModel'],
  ['mt', 'motorbikeType'],
]);

/**
 * Processes search filters to create a refined filters object for SEO requests used by Pito and Meti services.
 *
 * This function takes the raw filters from the search state along with their friendly labels and a flag
 * indicating whether to output labels or raw values. It iterates over each filter, performing the following:
 *
 * - Skips any filter whose key is listed in the `deniedFilters` array.
 * - Checks if the filter is considered important (as defined in the `importantFilters` map). If it is,
 *   the filter is mapped to a SEO-specific key and the corresponding value is selected based on the
 *   `filterOutputType` (either the friendly label or the ID value).
 * - Collects all non-important filters as a fallback.
 *
 * The function returns only the important filters if any are found; otherwise, it returns all the usable filters.
 */
const processFilters = (
  filters: Record<string, string>,
  filtersLabels: Record<string, string>,
  filterOutputType: 'labels' | 'values'
): Record<string, string> => {
  let hasImportant = false;
  const importantResult: Record<string, string> = {};
  const fallback: Record<string, string> = {};

  for (const [key, value] of Object.entries(filters)) {
    // Skip filters that are denied.
    if (deniedFilters.includes(key)) continue;

    if (importantFilters.has(key)) {
      hasImportant = true;

      const mappedKey = importantFilters.get(key)!;

      importantResult[mappedKey] =
        filterOutputType === 'labels' ? filtersLabels[key] : value;
    } else {
      // Collect non-important filters.
      fallback[key] = value;
    }
  }

  // Return only important filters if any were found; otherwise return all usable filters.
  return hasImportant ? importantResult : fallback;
};

/**
 * Transforms a listing query and search state into a structured SEO request.
 *
 * This function takes the search parameters from a listing query along with additional state details
 * (such as geographic information, category labels, and filter settings) and constructs an SEO request object.
 *
 * The resulting SEO request includes:
 * - The basic search details (query text, category, and category label).
 * - Geographic information: region, province, and city, along with their friendly labels derived from the geo data.
 * - The advertisement type and its corresponding friendly label.
 * - Pagination information.
 *
 * Additionally, if filters are provided in the search state it will process them with the processFilters function.
 */
export const mapListingQueryToSeoRequest = (
  query: ListingQuery,
  searchState: SearchState,
  filterOutputType: 'labels' | 'values' = 'labels'
): SeoRequest => {
  const { q, category, region, city, town, type, o } = query;

  const {
    geo: {
      region: { value: regionValue } = {},
      city: { value: cityValue } = {},
      town: { value: townValue } = {},
    },
    filters,
    category: { label: categoryLabel },
  } = searchState;

  const seoReq: SeoRequest = {
    searchQuery: q,
    category,
    categoryLabel,
    region,
    regionLabel: regionValue,
    province: city,
    provinceLabel: cityValue,
    city: town,
    cityLabel: townValue,
    adType: type,
    pagination: o || undefined,
  };

  // Return early if not filters are provided
  if (!filters) return seoReq;

  // Process filters
  const { filtersLabels } = searchState;
  seoReq.filters = processFilters(filters, filtersLabels, filterOutputType);

  // Pass the friendly name of the ad type to Meti
  seoReq.adTypeLabel = filtersLabels['t'];

  return seoReq;
};
